import { Button } from "@/components/ui/button";
import { performDeeplinkRedirect } from "@/lib/deeplink-utils";
import {
  getAppId,
  storeAppIdFallback,
  getStoredAppIdFallback,
} from "@/lib/token-utils";
import { getGameDataById } from "@/lib/game-config-utils";
import { useEffect, useState, useRef } from "react";
import type { GameData } from "@/interfaces";
import { buildRedirectUrl } from "@/lib/redirect-utils";
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import Icons from "@/components/Icons";
import { useStyleTheme } from "@/hooks/useStyleTheme";
import { useAnalytics } from "@/hooks/useAnalytics";
import {
  getCheckoutAnalyticsData,
  clearCheckoutAnalyticsData,
  safeTrack,
} from "@/services/AnalyticsService";

const SuccessPage = () => {
  const [gameData, setGameData] = useState<GameData | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(true);
  const [lottieError, setLottieError] = useState(false);
  const [lottieTimeoutId, setLottieTimeoutId] = useState<NodeJS.Timeout | null>(null);
  const [isNativeAppRedirect, setIsNativeAppRedirect] = useState(false);
  const [isGameDataLoaded, setIsGameDataLoaded] = useState(false);
  const theme = useStyleTheme(gameData);
  const { track, isEnabled } = useAnalytics();
  const hasTrackedSuccessRef = useRef(false);
  const successImagePath =
    theme.success.type === "image"
      ? theme.success.imagePath || gameData?.success_img
      : undefined;

  // Reset lottie error when theme changes (when gameData loads)
  useEffect(() => {
    if (isGameDataLoaded) {
      setLottieError(false);
    }
  }, [isGameDataLoaded, theme.success.lottiePath]);

  // Track successful purchase
  useEffect(() => {
    if (!isEnabled || !isGameDataLoaded || hasTrackedSuccessRef.current) {
      return;
    }
    hasTrackedSuccessRef.current = true;
    const urlParams = new URLSearchParams(window.location.search);
    const isCardDetailsSaved = urlParams.get('is_card_details_saved') === 'true';

    const analyticsData = getCheckoutAnalyticsData();
    const paymentMethod = analyticsData.payment_method || urlParams.get('payment_method') || 'card';

    safeTrack(track, 'purchase_successful', {
      ...analyticsData,
      payment_method: paymentMethod,
      is_card_details_saved: isCardDetailsSaved,
    });

    // Clear stored analytics data after tracking
    clearCheckoutAnalyticsData();
  }, [isEnabled, isGameDataLoaded, track]);

  useEffect(() => {
    // Set a timeout fallback for Lottie loading
    const lottieTimeout = setTimeout(() => {
      setLottieError(true);
    }, 3000); // 3 seconds timeout
    setLottieTimeoutId(lottieTimeout);

    // Check payment status from URL parameters first
    const urlParams = new URLSearchParams(window.location.search);
    const paymentIntentStatus = urlParams.get('payment_intent_status');
    const redirectStatus = urlParams.get('redirect_status');
    
    // If payment failed, redirect to error page
    if (redirectStatus === 'failed' || paymentIntentStatus === 'failed') {
      window.location.href = buildRedirectUrl('error', 'Payment failed. Please try again or use a different payment method.');
      return;
    }

    // Try to get gameData from multiple sources
    const getGameDataFromSources = (): GameData | null => {
      // 1. Try from URL token
      const appIdFromUrl = getAppId("url");
      if (appIdFromUrl) {
        storeAppIdFallback(appIdFromUrl); // Store for future fallback
        const gameDataFromAppId = getGameDataById(appIdFromUrl);
        if (gameDataFromAppId) return gameDataFromAppId;
      }

      // 2. Try from sessionStorage (existing logic)
      const storedGameData = sessionStorage.getItem("gameData");
      if (storedGameData) {
        try {
          return JSON.parse(storedGameData);
        } catch (error) {
          console.error("Failed to parse stored game data:", error);
        }
      }

      // 3. Try from localStorage fallback
      const storedAppId = getStoredAppIdFallback();
      if (storedAppId) {
        const gameDataFromStoredAppId = getGameDataById(storedAppId);
        if (gameDataFromStoredAppId) return gameDataFromStoredAppId;
      }

      return null;
    };

    const currentGameData = getGameDataFromSources();
    setGameData(currentGameData);
    setIsGameDataLoaded(true);

    // Check immediately if this is a native app redirect
    const nativeReturnUrl = sessionStorage.getItem('native_return_url');
    if (nativeReturnUrl) {
      setIsNativeAppRedirect(true);
    }

    // Show success message for 3000ms (3 seconds) then redirect
    const timer = setTimeout(() => {
      setShowSuccessMessage(false);

      // Check for native return URL from wrapped flow (native apps)
      const nativeReturnUrl = sessionStorage.getItem('native_return_url');
      
      if (nativeReturnUrl) {
        // Clear the stored URL to avoid reusing it
        sessionStorage.removeItem('native_return_url');
        // Redirect to native app using the provided return URL
        window.location.href = nativeReturnUrl;
        return;
      }

      // Use the new deeplink utilities for redirect
      const performRedirect = async () => {
        const gameId = currentGameData?.game_id;
        await performDeeplinkRedirect({
          gameId,
          gameData: currentGameData || undefined,
          fallbackTimeoutMs: 2000,
          source: "any",
        });
      };

      performRedirect();
    }, 3000);

    return () => {
      clearTimeout(timer);
      clearTimeout(lottieTimeout);
    };
  }, []);

  const handleRedirect = async () => {
    // Check for native return URL from wrapped flow (native apps)
    const nativeReturnUrl = sessionStorage.getItem('native_return_url');
    
    if (nativeReturnUrl) {
      // Clear the stored URL to avoid reusing it
      sessionStorage.removeItem('native_return_url');
      // Redirect to native app using the provided return URL
      window.location.href = nativeReturnUrl;
      return;
    }

    const gameId = gameData?.game_id;
    await performDeeplinkRedirect({
      gameId,
      gameData: gameData || undefined,
      fallbackTimeoutMs: 2000,
      source: "any",
    });
  };

  const handleManualRedirect = () => {
    handleRedirect();
  };


  if (!showSuccessMessage) {
    return (
      <section className="flex flex-col justify-center pt-[17px] pb-[21px]">
        <div className="flex flex-col justify-center items-center text-center px-3 w-full">
          <h1 className="text-[20px] text-[#423F3B] font-bold mb-[22px]">
            Redirecting...
          </h1>
          <p className="text-[#A2998B] mb-11">
            {isNativeAppRedirect 
              ? "Returning to your app..." 
              : "Please wait while we redirect you back to the game."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex flex-col pt-[17px] pb-[21px] h-full landscape:justify-between">
      {/* <p className="px-5 text-[#A2998B] text-[14px] mb-[75px] landscape:mb-1">
        {gameData?.checkout_title}
      </p> */}

      <div className="flex flex-col items-center text-center px-3 w-full h-full landscape:flex-1">
        <div className="flex-grow flex flex-col justify-center items-center landscape:flex-1">
          <div className="h-[150px] w-[150px] mb-5 landscape:h-[101px] landscape:w-[106px] landscape:mb-2 flex items-center justify-center">
            {!isGameDataLoaded ? (
              // Show loading placeholder while gameData loads
              <div className="w-full h-full flex items-center justify-center">
                <div className="animate-pulse bg-gray-200 rounded-full w-24 h-24" />
              </div>
            ) : theme.success.type === "image" && successImagePath ? (
              // Use themed success image
              <img 
                src={successImagePath} 
                alt="Success" 
                className="w-full h-full object-contain"
              />
            ) : theme.success.type === "lottie" && !lottieError ? (
              <DotLottieReact
                key={theme.success.lottiePath || "default-lottie"}
                src={theme.success.lottiePath || "/lottie/success-icon.lottie"}
                loop={false}
                autoplay
                className="w-full h-full"
                onLoad={() => {
                  // Clear timeout if Lottie loads successfully
                  if (lottieTimeoutId) {
                    clearTimeout(lottieTimeoutId);
                    setLottieTimeoutId(null);
                  }
                }}
                onError={() => setLottieError(true)}
              />
            ) : (
              // Fallback to successCheck icon from Icons component
              <Icons type="successCheck" size={120} />
            )}
          </div>
          <h1 className="text-[20px] text-[#423F3B] text-center font-['Poppins'] font-bold leading-[normal] text-edge-cap leading-trim-both mb-[12px] landscape:mb-2">
            Purchase Successful
          </h1>
          <div className="">
            <p className="w-full text-[#A2998B] text-center font-['Poppins'] text-[16px] font-normal leading-[normal] text-edge-cap leading-trim-both">
              Thank you for your order!
              <br />
              {isNativeAppRedirect 
                ? "You will be returned to your app in a few seconds." 
                : "You will be automatically redirected to the game in a few seconds."}
            </p>
          </div>
        </div>
        <div className="landscape:flex-shrink-0 w-full landscape:max-w-sm landscape:mx-auto">
          <Button
            variant="backToGame"
            onClick={handleManualRedirect}
            className="w-full landscape:mb-2"
            theme={theme}
          >
            Back to {gameData?.game_name || "the game"}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SuccessPage;
